import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory, useParams } from "react-router-dom";
import Page from "@app/components/page/page";
import ReportDocumentsGroup from "@app/components/setup/report-documents/group";
import ToggleReportDocuments from "@app/components/setup/report-documents/toggle";
import StylesOverview from "@app/components/setup/report-documents/styles/overview";
import CerConfigModal from "@app/components/cer-config";
import notify from "@app/components/notify";

import setup from "@app/state/store/projects/setup";
import projectStore from "@app/state/store/projects/details";
import store from "@app/state/store/report-document/report-documents";
import configClipboardStore from "@app/state/store/config-clipboard";
import cerConfigStore from "@app/state/store/cer-config";
import applicationStore from "@app/state/store/application";
import { AI_TEMPLATES, aiTemplateOf } from "@app/constants";

const SetupReportDocuments = observer(() => {
    const history = useHistory();
    const { id } = useParams();
    const config = setup.config;
    const clientId = projectStore.data.client._id;
    const [clientTemplates, setClientTemplates] = useState();
    const readonly = setup.isComplete;

    useEffect(() => {
        store.setProjectId(id);
        store.loadReportDocuments();

        return () => {
            store.reset();
        };
    }, [id]);

    useEffect(() => {
        (async () => {
            const templates = await store.getClientReportDocuments(clientId);
            setClientTemplates(templates);
        })();
    }, [clientId]);

    if (!store.reportDocuments || !config) {
        return;
    }

    // Update Project Configuration - Enable/Disable Source Documents.
    const updateConfig = async (data) => {
        const updates = {
            reportDocuments: {
                ...config.reportDocuments,
                ...data,
            },
        };

        config.set(updates);
        await setup.updateConfig(updates);
    };

    const close = () => {
        history.push("../setup");
    };

    const update = async (type, entry, args = {}) => {
        // reset the copy clipboard
        configClipboardStore.paste("reportDocument");

        try {
            const result = await store.updateReportDocument(type, entry);
            await store.loadReportDocuments();
            return result;
        } catch (ex) {
            const message = args.error || "Error saving the report document configuration";
            notify.error(message);
            return null;
        }
    };

    /**
     * Open the config wizard for an AI report document. It needs the project
     * status too: a draft project has no source documents to generate from, so a
     * run can be configured there but not started.
     */
    const openCerConfig = (reportDocumentId, template, hasPreviousRun = false) => {
        cerConfigStore.open({
            projectId: id,
            reportDocumentId,
            template,
            projectStatus: projectStore.data?.status,
            hasPreviousRun,
        });
    };

    /**
     * The client's own templates, plus the built-in AI ones.
     *
     * `GET /client/:id/report-documents/list` returns every document the client
     * owns, not only its client-level templates — so the project's own AI
     * documents would otherwise show up here as copyable "templates". Copying one
     * is harmless (the copy path strips the AI identity) but confusing.
     */
    const buildTemplateOptions = () => {
        const own = (clientTemplates || []).filter((t) => !t.aiGenerated);

        if (!applicationStore.enableCer) {
            return own;
        }

        return [...AI_TEMPLATES, ...own];
    };

    const onCopy = (reportDocumentId) => {
        const copy = {
            id: reportDocumentId,
            sourceProjectId: id,
            sourceClientId: projectStore.data.client._id,
        };
        configClipboardStore.copyReportDocument(copy);
    };

    const onPaste = async () => {
        const pasteData = configClipboardStore.paste("reportDocument");
        await projectStore.pasteReportDocument(id, pasteData);
        await store.loadReportDocuments();
    };

    const onStyleEdit = () => {
        history.push(`./report-documents/styles`);
    };

    const copied =
        configClipboardStore.reportDocument.id &&
        configClipboardStore.reportDocument.sourceProjectId &&
        configClipboardStore.reportDocument.sourceClientId === projectStore.data.client._id;

    return (
        <Page>
            <Page.Header closable onClose={close}>
                <Page.Header.Left shrink>
                    <Page.Title>Report Documents</Page.Title>
                </Page.Header.Left>

                <Page.Header.Right></Page.Header.Right>
            </Page.Header>

            <Page.Body maxWidth={1000}>
                <ToggleReportDocuments
                    config={config.reportDocuments}
                    onChange={(data) => {
                        updateConfig(data);
                    }}
                    readonly={readonly}
                />

                {config.reportDocuments?.enabled && (
                    <>
                        <StylesOverview config={config} onEdit={onStyleEdit} readonly={readonly} />
                        <ReportDocumentsGroup
                            list={store.reportDocuments}
                            title="Report Documents"
                            onChange={async (type, entry) => {
                                // An AI template option is a frontend sentinel,
                                // not a document to copy — swap it for the flags it
                                // stands for before it reaches the API.
                                const aiTemplate =
                                    type === "create" ? aiTemplateOf(entry.clientTemplate) : null;
                                const { clientTemplate, ...rest } = entry;

                                const result = await update(type, {
                                    project: id,
                                    client: clientId,
                                    ...rest,
                                    ...(aiTemplate
                                        ? { aiGenerated: true, aiTemplate }
                                        : { clientTemplate }),
                                });

                                // Collecting the config is the rest of "create an
                                // AI generated document".
                                if (aiTemplate && result?._id) {
                                    openCerConfig(result._id, aiTemplate);
                                }
                            }}
                            onCopy={onCopy}
                            onPaste={onPaste}
                            onConfigure={(entry) =>
                                openCerConfig(entry._id, entry.aiTemplate, Boolean(entry.cerRunId))
                            }
                            copied={copied}
                            clientTemplates={buildTemplateOptions()}
                            readonly={readonly}
                        />
                    </>
                )}
            </Page.Body>

            <CerConfigModal />
        </Page>
    );
});

export default SetupReportDocuments;
